import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { Bet } from "../../../../../../../../shared/src/lib/types/bet.type";
import { CashOutService } from "../../../../../../../../shared/src/lib/services/cash-out.service";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { FeatureFlagDirective } from "../../../../../../../../shared/src/lib/directives/feature-flag.directive";
import { BetPlacementService } from "../../../../../../../../shared/src/lib/services/bet-placement.service";
import { BehaviorSubject, combineLatest, forkJoin, Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { safeParseFloat } from "../../../../../../../../shared/src/lib/utils/common";
import { LoaderService } from "../../../../../../../../shared/src/lib/services/loader.service";
import { PlacementOpsService } from "../../../../betting-manager/placement-manager/placement-ops.service";
import { ApiService } from "../../../../../../../../shared/src";
import { MarketOpsService } from "../market-ops.service";
import { WhiteLabelConfigService } from "../../../../../../../../shared/src/lib/services/white-label-config.service";

@Component({
  selector: "app-cash-out-offer",
  imports: [AsyncPipe, FeatureFlagDirective, DecimalPipe, NgClass],
  templateUrl: "./cash-out-offer.component.html",
  styleUrl: "./cash-out-offer.component.scss",
})
export class CashOutOfferComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) market!: any;
  @Input() runnerUpdates: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly destroy$ = new Subject<void>();
  placementResponse = inject(PlacementOpsService);
  cashOutService = inject(CashOutService);
  apiService = inject(ApiService);
  loader = inject(LoaderService);
  placementService = inject(BetPlacementService);
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  wlSlipSettings: any;
  isCashOutDisabled = false;
  currentPlacement: any = null;
  placementBackup: any = null;
  offer$ = new BehaviorSubject<any>(null);

  get userId() {
    return this.apiService.getUserToken()?.user?.ID;
  }

  get walletId() {
    return this.apiService.getUserWalletId();
  }

  get isBettingBlocked() {
    return this.apiService.getUserToken()?.user?.IS_BLOCKED === "true";
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["market"]) {
      this.getCashOutOffer(changes["market"]?.currentValue);
    }
  }

  ngOnInit() {
    this.getCashOutOffer(this.market);
    this.disableCashOut()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => (this.isCashOutDisabled = state));
    this.getSlipChange();
    this.setStakeValues();
    this.getCurrentPlacement();
  }

  getCurrentPlacement() {
    this.placementService.currentPlacement.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (!result) this.placementBackup = null;
        this.currentPlacement = result;
      },
    });
  }

  getSlipChange() {
    this.placementService.changeNotifier$.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      if (type === "slipChange") {
        this.setStakeValues();
      }
    });
  }

  setStakeValues() {
    this.wlSlipSettings = this.placementService.getWLSlipSettings();
  }

  getMarketBets(marketId?: string): Observable<Bet[]> {
    return this.placementService.openBetsSubject.pipe(
      map(
        (data) =>
          data?.filter((bet: Bet) => {
            const isMatched = safeParseFloat(bet?.stakeRemaining) === 0 && safeParseFloat(bet?.stakeMatched) > 0;
            const betMarketId = bet?.topic ?? bet?.marketId;
            const marketIdMatches = betMarketId === marketId;
            return isMatched && marketIdMatches;
          }) || [],
      ),
    );
  }

  getAllMarketBets(marketId?: string): Observable<Bet[]> {
    return this.placementService.openBetsSubject.pipe(
      map(
        (data) =>
          data?.filter((bet: Bet) => {
            const betMarketId = bet?.topic ?? bet?.marketId;
            const marketIdMatches = betMarketId === marketId;
            return marketIdMatches;
          }) || [],
      ),
    );
  }

  getCashOutOffer(market: any) {
    combineLatest([this.runnerUpdates, this.getMarketBets(market?.topic ?? market?.marketId)])
      .pipe(
        map(([runnerUpdate, marketBets]) => {
          if (marketBets.length) {
            const offer = this.cashOutService.generateOffer(market, runnerUpdate, marketBets);
            // console.log(offer);
            this.offer$.next(offer);
            this.placementResponse.resetStatus();
          } else {
            this.offer$.next(null);
          }
        }),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  disableCashOut() {
    return combineLatest([
      this.offer$,
      this.getMarketBets(this.market?.marketId),
      this.getAllMarketBets(this.market?.marketId),
      this.loader.isLoading("bf_placebet"),
      this.loader.isLoading("current-user-order"),
      this.loader.isLoading("refresh_exposure"),
    ]).pipe(
      map(([offer, betsForMarket, allMarketBets, placingBet, fetchingBets, refreshingExposure]) => {
        const isLoading = placingBet || fetchingBets || refreshingExposure;
        const isPlacingBet = this.placementResponse.isPlacingBet;
        const isOnTimeout = this.placementResponse.timeout;
        const isBettingBlocked = this.isBettingBlocked;
        const isStakeInvalid = () => {
          const minCashOutValue = 0;
          const minWlStake = safeParseFloat(this.wlSlipSettings?.quickBetAmounts?.[0]);
          return offer?.bets?.some((item: any) => {
            const size = item.SIZE ?? 0;
            const min = Number.isFinite(minCashOutValue) ? safeParseFloat(minCashOutValue) : null;
            const stakeIsLtMinWlStake = size < minWlStake;

            // if the market is Match odds - 0% commission
            const stakeIsLtMin = typeof min === "number" && min > 0 ? size < min : false;

            return item?.descriptionPrefix === "Bookmaker" ? stakeIsLtMin : stakeIsLtMinWlStake;
          });
        };
        const isCashOutPnLLtMinPnL = () => {
          const cashOutPnL = offer?.profit;
          return cashOutPnL < offer?.minPnL;
        };

        const isBalanceInsufficient = this.cashOutService.isBalanceInsufficient(offer?.bets, allMarketBets);

        // console.log("isLoading", isLoading);
        // console.log("isPacingBet", isPlacingBet);
        // console.log("isOnTimeout", Boolean(isOnTimeout));
        // console.log("isBettingBlocked", isBettingBlocked);
        // console.log("isStakeInvalid", isStakeInvalid());
        // console.log("isCashOutPnLLtMinPnL", isCashOutPnLLtMinPnL());
        // console.log("isBalanceInsufficient", isBalanceInsufficient);

        return isLoading || isPlacingBet || Boolean(isOnTimeout) || isBettingBlocked || isStakeInvalid() || isCashOutPnLLtMinPnL() || isBalanceInsufficient;
      }),
    );
  }

  handleSubmit(bets: any[]) {
    if (this.isCashOutDisabled) return;
    this.resetPlacement();
    // console.log(this.form.value);
    // this.isLoading = true;
    this.placementResponse.isPlacingBet = true;

    // console.log(this.cashOutOffer?.cashOutBets);
    const requests = bets?.map((bet: any) =>
      this.placementService.performPlacement(
        {
          ...bet,
          USER_ID: this.userId,
          WALLET_ID: this.walletId,
        },
        "cash-out",
      ),
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (responses: any) => {
          responses.forEach((response: any, index: any) => {
            if (response.status !== "ok") {
              this.placementResponse.handleError(`Error in placement for bet ${index}: ${JSON.stringify(response?.result)}`);
            } else {
              const betId = response?.result?.[0]?.ID;
              if (this.placementResponse.isPlacingBet) {
                this.placementResponse.setTransactionTimeout(betId);
              }
            }
          });

          // this.isLoading = false;
        },
        error: (err) => {
          console.error("Error processing placements", err);
          this.resetPlacement();
          // this.isLoading = false;
        },
      });
  }

  handleLivePnl(event: any, bets: any[]) {
    if (this.isCashOutDisabled) return;
    if (event?.type === "mouseenter") {
      const cashOutBets = bets?.map((each: any) => ({
        type: each?.BETTING_TYPE,
        data: { ...each, price: each?.PRICE, stake: each?.SIZE, runners: this.market?.runners },
      }));
      if (this.currentPlacement) this.placementBackup = this.currentPlacement;
      this.placementService.currentPlacement.next(cashOutBets);
    } else {
      this.resetPlacement();
    }
  }

  resetPlacement() {
    this.placementService.currentPlacement.next(this.placementBackup ?? null);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
